import Context from "../app.context";
import { sanitizeUserName } from "../utils/users";
import { ICredentialsWithServer } from "../utils/credentialsStore";

export enum ValueType {
    DATE = "DATE",
    STRING = "STRING"
}

export interface IHosClock {
    label: string;
    value: string;
    valueType: ValueType;
    durationSeconds: null | number;
    /*Indicates the important clock. Consumers may interpret this flag in multiple ways,
    but one possible use is to determine which clock to display in a compact view layout that only permits a single clock to be shown.*/
    important: boolean;
    /* Indicates which clock most tightly limits the time a driver can spend driving.
    Consumers may interpret this flag in multiple ways, but one possible use is to indicate where a driver needs to plan to shut down when planning a route. */
    limitsDrivingRange: boolean;
}

export interface IHosStatus {
    username?: string;
    clocks: IHosClock[];
    manageAction: OpenCabDeepLink.MANAGE_ACTION;
    logoutAction: OpenCabDeepLink.LOGOUT_ACTION;
}

export interface IHosTeamOpenCabAvailability {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    KEY_HOS: IHosStatus;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    KEY_TEAM_HOS: IHosStatus[];
}

export interface IHosTeamOpenCabAvailabilityV0Dot4 {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    KEY_HOS: string; // A stringified IHosStatus object
    // eslint-disable-next-line @typescript-eslint/naming-convention
    KEY_TEAM_HOS: string; // A stringified IKeyTeamHosV0Dot4 object
}

export interface IKeyTeamHosV0Dot4 {
    teamHosData: IHosStatus[];
}

export interface IDriverSessionPayload {
    username: string;
    loginCredentials: ILoginCredentialsPayload;
}

interface ILoginCredentialsPayload {
    authority: string;
    provider: string;
    token: string;
}

export interface IDriverSession {
    // external username
    username: string;
    loginCredentials: ILoginCredentials;
}

interface ILoginCredentials {
    authority: string;
    provider: string;
    token: IToken;
}

export interface IToken {
    path: string;
    credentials: ICredentials;
}

export interface IAlternateToken {
    path: string;
    username: string;
    sessionId: string;
    database: string;
}

export interface IUsernameMap {
    externalUsername: string;
    internalUsername: string;
}

export interface IDriver {
    username: string;
    driving: boolean;
}

export interface IDriverInfo {
    user: DeepReadonlyObject<IUser>;
    externalUsername: string;
    context: Context;
}

export enum ActionType {
    DEFAULT = 0,
    MANAGE_ACTION = 1,
    LOGOUT_ACTION = 2,
    ACTION_DRIVER_LOGIN = 3,
    ACTION_DRIVER_LOGOUT = 4,
    ACTION_IDENTITY_INFORMATION_CHANGED = 5
}

export interface IOpenCabEventDetail {
    action: number;
}

export const enum OpenCabDeepLink {
    MANAGE_ACTION = "geotabdrive://opencab/manage",
    LOGOUT_ACTION = "geotabdrive://opencab/logout"
}

const instanceOfIDriver = (obj: any): obj is IDriver => obj && typeof obj.username === "string" && typeof obj.driving === "boolean";
export const instanceOfIDriverArray = (obj: any): obj is IDriver[] => obj && Array.isArray(obj) && obj.every(instanceOfIDriver);

const instanceOfILoginCredentialsPayload = (obj: any): obj is ILoginCredentialsPayload =>
    obj && typeof obj.authority === "string" && typeof obj.provider === "string" && typeof obj.token === "string";
export const instanceOfIDriverSessionPayload = (obj: any): obj is IDriverSessionPayload =>
    obj && typeof obj.username === "string" && instanceOfILoginCredentialsPayload(obj.loginCredentials);
export const instanceOfIDriverSessionPayloadArray = (obj: any): obj is IDriverSessionPayload[] => obj && Array.isArray(obj) && obj.every(instanceOfIDriverSessionPayload);
const instanceOfICredentials = (obj: any): obj is ICredentials =>
    obj && typeof obj.userName === "string" && typeof obj.sessionId === "string" && typeof obj.database === "string";
const instanceOfIToken = (obj: any): obj is IToken =>
    obj && typeof obj.path === "string" && instanceOfICredentials(obj.credentials);
const instanceOfITokenAlt = (obj: any): obj is IAlternateToken =>
    obj && typeof obj.path === "string" && typeof obj.username === "string" && typeof obj.sessionId === "string" && typeof obj.database === "string";

function getDriverSessionFromPayload(driverSessionPayload: IDriverSessionPayload): IDriverSession {
    const username = sanitizeUserName(driverSessionPayload.username);
    const { authority, provider, token } = driverSessionPayload.loginCredentials;
    let parsedObj: IToken | IAlternateToken = JSON.parse(token);
    let tokenObj: IToken = instanceOfITokenAlt(parsedObj)
        ? { path: parsedObj.path, credentials: { userName: parsedObj.username, sessionId: parsedObj.sessionId, database: parsedObj.database }}
        : JSON.parse(token);
    if (!instanceOfIToken(tokenObj)) {
        throw new Error("Invalid token object");
    }
    tokenObj.credentials.userName = sanitizeUserName(tokenObj.credentials.userName);
    const loginCredentialsObj: ILoginCredentials = { authority, provider, token: tokenObj };
    return { username, loginCredentials: loginCredentialsObj };
}

export function getDriverSessionArrayFromPayloadArray(driverSessionPayloadArray: IDriverSessionPayload[]): IDriverSession[] {
    return driverSessionPayloadArray.map(getDriverSessionFromPayload);
}

export function getCredentialsWithServerFromDriverSession (driverSession: IDriverSession): ICredentialsWithServer {
    const token = driverSession.loginCredentials.token;
    return { server: token.path, credentials: { ...token.credentials } };
}